import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import authService from "../../services/auth.service";
import { supabase } from "../../utils/supabase";
import Card from "../UI/Card";
import PromptForm from "../PromptForm/PromptForm";
import ScriptModal from "../Scripts/ScriptModal";
import {
  FaUser,
  FaSync,
  FaCopy,
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaLinkedin,
  FaPinterest,
  FaReddit,
} from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import { RiTwitterXFill } from "react-icons/ri";

const DashboardContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const glow = keyframes`
  0% {text-shadow: 0 0 5px rgba(255, 215, 0, 0.3), 0 0 10px rgba(255, 215, 0, 0.2), 0 0 15px rgba(255, 215, 0, 0.1);}
  50% {text-shadow: 0 0 10px rgba(255, 215, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.3), 0 0 30px rgba(255, 215, 0, 0.2);}
  100% {text-shadow: 0 0 5px rgba(255, 215, 0, 0.3), 0 0 10px rgba(255, 215, 0, 0.2), 0 0 15px rgba(255, 215, 0, 0.1);}
`;

const LogoShine = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;

  &::after {
    content: "";
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 2px;
    background: linear-gradient(
      90deg,
      rgba(212, 175, 55, 0) 0%,
      rgba(212, 175, 55, 1) 50%,
      rgba(212, 175, 55, 0) 100%
    );
  }

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }

  h1 {
    font-family: "Playfair Display", serif;
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    position: relative;
    background: linear-gradient(
      90deg,
      #bf953f,
      #fcf6ba,
      #b38728,
      #fbf5b7,
      #aa771c
    );
    background-size: 200% auto;
    color: transparent;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${LogoShine} 5s linear infinite;
    letter-spacing: 2px;

    &::before,
    &::after {
      content: "";
      position: absolute;
      width: 40px;
      height: 2px;
      background: linear-gradient(
        90deg,
        rgba(212, 175, 55, 0) 0%,
        rgba(212, 175, 55, 1) 100%
      );
      top: 50%;
    }

    &::before {
      left: -60px;
      transform: translateX(-100%);
    }

    &::after {
      right: -60px;
      transform: translateX(100%);
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
      &::before,
      &::after {
        display: none;
      }
    }

    @media (max-width: 480px) {
      font-size: 2rem;
      margin-bottom: 0.5rem;
    }
  }

  p {
    font-family: "Cormorant Garamond", serif;
    color: var(--text-secondary);
    font-size: 1.3rem;
    max-width: 600px;
    margin: 0 auto;
    font-weight: 300;
    letter-spacing: 1px;
    animation: ${glow} 4s ease-in-out infinite;

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }

    @media (max-width: 480px) {
      font-size: 1rem;
      max-width: 320px;
    }
  }
`;

const WelcomeSection = styled.div`
  margin-bottom: 2.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;

  h2 {
    margin-bottom: 0.5rem;
    font-family: "Playfair Display", serif;
    font-size: 2.2rem;
    background: linear-gradient(
      90deg,
      #bf953f,
      #fcf6ba,
      #b38728,
      #fbf5b7,
      #aa771c
    );
    background-size: 200% auto;
    color: transparent;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    color: var(--text-secondary);
    font-size: 1.1rem;
    max-width: 800px;
  }
`;

const UserAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--background-tertiary);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    font-size: 2rem;
    color: var(--text-secondary);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionDivider = styled.div`
  position: relative;
  margin: 3rem 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(212, 175, 55, 0) 0%,
    rgba(212, 175, 55, 0.5) 50%,
    rgba(212, 175, 55, 0) 100%
  );

  &::after {
    content: "Your Scripts";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: var(--background-color);
    padding: 0 1.5rem;
    font-family: "Playfair Display", serif;
    color: var(--primary-color);
    font-size: 1.3rem;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 1rem;
  border-radius: var(--border-radius);
  border: 1px solid rgba(212, 175, 55, 0.1);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    text-align: left;
  }

  th {
    background-color: var(--background-tertiary);
    color: var(--primary-color);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 1px;
    border-bottom: 1px solid rgba(212, 175, 55, 0.15);
  }

  tr {
    cursor: pointer;
    border-bottom: 1px solid rgba(212, 175, 55, 0.05);
    transition: all 0.2s ease;
  }

  tr:hover td {
    background-color: rgba(212, 175, 55, 0.05);
  }

  tr:last-child {
    border-bottom: none;
  }

  td {
    color: var(--text-secondary);
    vertical-align: middle;
  }
`;

const TableAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--background-tertiary);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    font-size: 1.2rem;
    color: var(--text-secondary);
  }
`;

const ScriptPreview = styled.div`
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
  line-height: 1.4;

  @media (max-width: 768px) {
    max-width: 180px;
  }
`;

const PromptPreview = styled.div`
  font-size: 0.85rem;
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
  line-height: 1.2;
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    max-width: 180px;
  }
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  background: transparent;
  border: 1px solid var(--border-color);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    color: var(--primary-color);
    border-color: var(--primary-color);
    background-color: rgba(212, 175, 55, 0.05);
  }

  svg {
    font-size: 0.9rem;
  }
`;

const CreatorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .creator-name {
    font-size: 0.9rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-tertiary);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const LastUpdated = styled.div`
  text-align: center;
  margin-top: 1rem;
  font-size: 0.8rem;
  color: var(--text-tertiary);
`;

const PlatformsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PlatformIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.9rem;
  background: rgba(0, 0, 0, 0.2);
  color: ${(props) => props.color || "var(--text-secondary)"};
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [userName, setUserName] = useState("Distinguished Creator");
  const [userProfile, setUserProfile] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [scriptCreators, setScriptCreators] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selectedScript, setSelectedScript] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Platform configuration with brand colors
  const platformConfig = {
    instagram: { icon: FaInstagram, color: "#E1306C" },
    youtube: { icon: FaYoutube, color: "#FF0000" },
    tiktok: { icon: SiTiktok, color: "#ffffff" },
    facebook: { icon: FaFacebook, color: "#1877F2" },
    twitter: { icon: RiTwitterXFill, color: "#ffffff" },
    x: { icon: RiTwitterXFill, color: "#ffffff" },
    linkedin: { icon: FaLinkedin, color: "#0A66C2" },
    pinterest: { icon: FaPinterest, color: "#E60023" },
    reddit: { icon: FaReddit, color: "#FF4500" },
  };

  useEffect(() => {
    // Set the user's name using the enhanced method
    setUserName(authService.getUserDisplayName());

    // Add observer for profile changes
    const profileObserver = (profile) => {
      setUserProfile(profile);
      if (profile?.full_name) {
        setUserName(profile.full_name);
      }

      // Set avatar URL if available in profile
      if (profile?.avatar_url) {
        setAvatarUrl(profile.avatar_url);
      }
    };

    authService.addProfileObserver(profileObserver);

    // Fetch profile if needed
    if (currentUser && !authService.userProfile) {
      authService.fetchUserProfile(currentUser.id);
    }

    // Check if avatar URL is available in user metadata (for Google auth)
    if (currentUser?.user_metadata?.avatar_url) {
      setAvatarUrl(currentUser.user_metadata.avatar_url);
    } else if (currentUser?.user_metadata?.picture) {
      setAvatarUrl(currentUser.user_metadata.picture);
    }

    return () => {
      authService.removeProfileObserver(profileObserver);
    };
  }, [currentUser]);

  // Function to fetch scripts
  const fetchScripts = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("auto-scripts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        setError("Failed to load scripts. Please try again later.");
        console.error("Error fetching scripts:", error);
      } else {
        console.log("Scripts fetched:", data?.length || 0);

        // Debug: Log the first script to check field names
        if (data && data.length > 0) {
          console.log("Sample script data:", data[0]);
          console.log("Refined prompt field:", data[0].refined_prompt);

          // If the refined prompt field is missing, check for alternative names
          if (data[0].refined_prompt === undefined) {
            console.log("Checking alternative field names:");
            console.log("- refinedPrompt:", data[0].refinedPrompt);
            console.log("- refined:", data[0].refined);
          }
        }

        setScripts(data || []);

        // Extract unique user IDs from scripts
        const userIds = [
          ...new Set(data?.map((script) => script.user_uuid) || []),
        ];

        // Fetch profile info for all script creators
        await fetchCreatorProfiles(userIds);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  };

  // Fetch profile information for creators
  const fetchCreatorProfiles = async (userIds) => {
    try {
      // Only fetch if we have user IDs
      if (!userIds.length) return;

      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, email")
        .in("id", userIds);

      if (error) {
        console.error("Error fetching creator profiles:", error);
      } else if (data) {
        console.log("Creator profiles fetched:", data.length);
        // Create a map of user ID to profile data
        const creatorMap = {};
        data.forEach((profile) => {
          creatorMap[profile.id] = profile;
        });
        setScriptCreators(creatorMap);
      }
    } catch (err) {
      console.error("Error fetching creator profiles:", err);
    }
  };

  // Manual refresh handler
  const handleRefresh = () => {
    fetchScripts();
  };

  // Fetch all scripts from Supabase
  useEffect(() => {
    // Initial fetch
    fetchScripts();

    // Set up real-time subscription for all scripts
    const subscription = supabase
      .channel("public:auto-scripts")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "auto-scripts",
        },
        (payload) => {
          // Refresh the scripts when changes are detected
          fetchScripts();
        }
      )
      .subscribe();

    return () => {
      // Clean up subscription
      subscription.unsubscribe();
    };
  }, []);

  // Format platforms array for display as icons
  const formatPlatformIcons = (platforms) => {
    if (!platforms || platforms.length === 0) {
      return <span style={{ color: "var(--text-tertiary)" }}>None</span>;
    }

    return (
      <PlatformsContainer>
        {platforms.map((platform, index) => {
          const lowerPlatform = platform.toLowerCase();
          const config = platformConfig[lowerPlatform] || {
            icon: FaUser,
            color: "#ccc",
          };
          const Icon = config.icon;

          return (
            <PlatformIcon key={index} color={config.color} title={platform}>
              <Icon />
            </PlatformIcon>
          );
        })}
      </PlatformsContainer>
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);

    // Format: Feb 12, 2023
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get creator display name
  const getCreatorName = (userId) => {
    const profile = scriptCreators[userId];
    if (profile?.full_name) return profile.full_name;
    if (profile?.email) return profile.email.split("@")[0];
    return "Unknown Creator";
  };

  // Get creator avatar URL
  const getCreatorAvatar = (userId) => {
    return scriptCreators[userId]?.avatar_url || null;
  };

  // Get creator provider (auth method)
  const getCreatorProvider = (userId) => {
    if (userId === currentUser?.id) {
      return currentUser?.app_metadata?.provider || "email";
    }
    return null;
  };

  // Handle row click to open modal
  const handleRowClick = (script) => {
    setSelectedScript(script);
    setModalOpen(true);
  };

  // Get appropriate script preview
  const getScriptPreview = (script) => {
    if (!script.script) return "Processing...";

    // Clean the script - remove extra spaces, newlines, etc.
    const cleanText = script.script
      .trim()
      .replace(/\s+/g, " ")
      .substring(0, 70);

    return cleanText + (cleanText.length >= 70 ? "..." : "");
  };

  // Check for refined prompt field with hyphen
  const getRefinedPrompt = (script) => {
    return script["refined-prompt"] || "";
  };

  return (
    <DashboardContainer>
      {/* Beautiful Shining Title */}
      <Header>
        <h1>Script Generator</h1>
        <p>Crafting Exquisite Content For The Distinguished</p>
      </Header>

      {/* Welcome Section with Avatar */}
      <WelcomeSection>
        <UserAvatar>
          {avatarUrl ? <img src={avatarUrl} alt={userName} /> : <FaUser />}
        </UserAvatar>
        <UserInfo>
          <h2>Welcome, {userName}</h2>
          <p>
            Create exceptional content with our premium script generator. Enter
            your prompt below and select platforms to generate tailored scripts.
          </p>
        </UserInfo>
      </WelcomeSection>

      {/* Prompt Form Section */}
      <PromptForm />

      {/* Divider between form and table */}
      <SectionDivider />

      {/* Scripts Table Section */}
      <Card>
        <Card.Body>
          <TableHeader>
            <div></div> {/* Spacer for flex alignment */}
            <RefreshButton onClick={handleRefresh}>
              <FaSync /> Refresh Scripts
            </RefreshButton>
          </TableHeader>

          {loading ? (
            <LoadingState>
              <p>Loading scripts...</p>
            </LoadingState>
          ) : error ? (
            <EmptyState>
              <p>{error}</p>
            </EmptyState>
          ) : scripts.length > 0 ? (
            <>
              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>Creator</th>
                      <th>Content</th>
                      <th>Platforms</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scripts.map((script) => (
                      <tr
                        key={script.id}
                        onClick={() => handleRowClick(script)}
                      >
                        <td>
                          <CreatorInfo>
                            <TableAvatar>
                              {getCreatorAvatar(script.user_uuid) ? (
                                <img
                                  src={getCreatorAvatar(script.user_uuid)}
                                  alt={getCreatorName(script.user_uuid)}
                                />
                              ) : (
                                <FaUser />
                              )}
                            </TableAvatar>
                            <span className="creator-name">
                              {getCreatorName(script.user_uuid)}
                            </span>
                          </CreatorInfo>
                        </td>
                        <td>
                          <ScriptPreview>
                            {getScriptPreview(script)}
                          </ScriptPreview>
                          <PromptPreview>
                            {script.prompt
                              ? script.prompt.substring(0, 60) +
                                (script.prompt.length > 60 ? "..." : "")
                              : "No prompt"}
                          </PromptPreview>
                        </td>
                        <td>{formatPlatformIcons(script.platforms)}</td>
                        <td>{formatDate(script.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </TableContainer>
              <LastUpdated>
                Last updated: {lastRefresh.toLocaleTimeString()}
              </LastUpdated>
            </>
          ) : (
            <EmptyState>
              <p>No scripts found. Create your first script now!</p>
            </EmptyState>
          )}
        </Card.Body>
      </Card>

      {/* Script Details Modal */}
      <ScriptModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        script={selectedScript}
        creatorName={
          selectedScript ? getCreatorName(selectedScript.user_uuid) : ""
        }
        creatorAvatar={
          selectedScript ? getCreatorAvatar(selectedScript.user_uuid) : null
        }
        creatorProvider={
          selectedScript ? getCreatorProvider(selectedScript.user_uuid) : null
        }
        formattedDate={
          selectedScript ? formatDate(selectedScript.created_at) : ""
        }
      />
    </DashboardContainer>
  );
};

export default Dashboard;
